import { useEffect, useState } from 'react';
import { CoQuanThueFormattedData } from '@/features/danh-muc/khac/co-quan-thue/schema';
import { CoQuanThue, CoQuanThueResponse } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseCoQuanThueReturn {
  coQuanThues: CoQuanThue[];
  loading: boolean;
  addCoQuanThue: (data: CoQuanThueFormattedData) => Promise<void>;
  updateCoQuanThue: (uuid: string, data: CoQuanThueFormattedData) => Promise<void>;
  deleteCoQuanThue: (uuid: string) => Promise<void>;
  refreshCoQuanThue: () => Promise<void>;
}

export const useCoQuanThue = (intialCoQuanThue: CoQuanThue[] = []): UseCoQuanThueReturn => {
  const [coQuanThues, setCoQuanThues] = useState<CoQuanThue[]>(intialCoQuanThue);
  const [loading, setLoading] = useState<boolean>(false);

  const { entity } = useAuth();

  const fetchCoQuanThue = async () => {
    if (!entity?.slug) return;
    setLoading(true);

    try {
      const response = await api.get<CoQuanThueResponse>(`entities/${entity?.slug}/erp/tax-authorities/`);
      const mappedData: CoQuanThue[] = response.data.results;
      setCoQuanThues(mappedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tax authorities:', error);
    }
  };

  const addCoQuanThue = async (data: CoQuanThueFormattedData) => {
    if (!entity?.slug) return;
    setLoading(true);
    const reponse = await api.post(`entities/${entity?.slug}/erp/tax-authorities/`, {
      ma_cqt: data.ma_cqt,
      ten_cqt: data.ten_cqt,
      ma_cqt0: data.ma_cqt0 || null,
      status: data.status || '1'
    });

    const newCoQuanThue: CoQuanThue = reponse.data;
    setCoQuanThues(prev => [...prev, newCoQuanThue]);
    setLoading(false);
  };

  const updateCoQuanThue = async (uuid: string, data: CoQuanThueFormattedData) => {
    if (!entity?.slug) return;
    setLoading(true);
    const reponse = await api.patch(`entities/${entity?.slug}/erp/tax-authorities/${uuid}/`, {
      ma_cqt: data.ma_cqt,
      ten_cqt: data.ten_cqt,
      ma_cqt0: data.ma_cqt0 || null,
      status: data.status || '1'
    });

    const updatedCoQuanThue: CoQuanThue = reponse.data;
    setCoQuanThues(prev => prev.map(item => (item.uuid === uuid ? updatedCoQuanThue : item)));
    setLoading(false);
  };

  const deleteCoQuanThue = async (uuid: string) => {
    if (!entity?.slug) return;
    setLoading(true);
    await api.delete(`entities/${entity?.slug}/erp/tax-authorities/${uuid}/`);
    setCoQuanThues(prev => prev.filter(item => item.uuid !== uuid));
    setLoading(false);
  };

  useEffect(() => {
    fetchCoQuanThue();
  }, [entity?.slug]);

  return {
    coQuanThues,
    loading,
    addCoQuanThue,
    updateCoQuanThue,
    deleteCoQuanThue,
    refreshCoQuanThue: fetchCoQuanThue
  };
};
